import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Trash2,
  Palette,
  Clock,
  Coffee,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { SHIFT_COLORS, colorStyles, fmtHours } from "@/pages/schedule/helpers";

function calcDuration(startTime, endTime, breakMinutes) {
  if (!startTime || !endTime) return 0;
  const [sh, sm] = startTime.split(":").map(Number);
  const [eh, em] = endTime.split(":").map(Number);
  let s = sh * 60 + sm;
  let e = eh * 60 + em;
  if (e <= s) e += 24 * 60;
  const mins = Math.max(0, e - s - (Number(breakMinutes) || 0));
  return mins / 60;
}

export default function ShiftTemplateFormDialog({
  open,
  onOpenChange,
  initial,
  siteId,
  siteName,
  onSaved,
}) {
  const isEdit = !!initial?._id;
  const [name, setName] = useState("");
  const [startTime, setStartTime] = useState("06:00");
  const [endTime, setEndTime] = useState("14:00");
  const [unpaidBreak, setUnpaidBreak] = useState(0);
  const [color, setColor] = useState("indigo");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setName(initial.name || "");
      setStartTime(initial.start_time || "06:00");
      setEndTime(initial.end_time || "14:00");
      setUnpaidBreak(initial.unpaid_break_minutes || 0);
      setColor(initial.color || "indigo");
      setNotes(initial.notes || "");
    } else {
      setName("");
      setStartTime("06:00");
      setEndTime("14:00");
      setUnpaidBreak(0);
      setColor("indigo");
      setNotes("");
    }
  }, [open, initial]);

  const duration = useMemo(
    () => calcDuration(startTime, endTime, unpaidBreak),
    [startTime, endTime, unpaidBreak],
  );

  const onSubmit = async () => {
    if (!name.trim()) {
      toast.error("Add meg a műszak nevét.");
      return;
    }
    if (!siteId && !isEdit) {
      toast.error("Először válassz telephelyet.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        site_id: siteId,
        name: name.trim(),
        start_time: startTime,
        end_time: endTime,
        unpaid_break_minutes: Math.max(0, Number(unpaidBreak) || 0),
        color,
        notes: notes.trim() || undefined,
      };
      const data = isEdit
        ? (await api.patch(`/shift-templates/${initial._id}`, payload)).data
        : (await api.post(`/shift-templates`, payload)).data;
      toast.success(isEdit ? "Sablon frissítve." : "Új sablon létrehozva.");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm("Biztosan törlöd ezt a sablont?")) return;
    setDeleting(true);
    try {
      await api.delete(`/shift-templates/${initial._id}`);
      toast.success("Sablon törölve.");
      onSaved?.({ deleted: true, _id: initial._id });
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e) || "Törlés sikertelen.");
    } finally {
      setDeleting(false);
    }
  };

  const cs = colorStyles(color);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="overflow-hidden p-0 sm:max-w-md"
        data-testid="shift-template-dialog"
      >
        {/* HERO PREVIEW */}
        <div
          className={cn(
            "relative overflow-hidden px-6 pb-5 pt-6",
            cs.block,
          )}
        >
          {/* dot-matrix */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:radial-gradient(circle_at_1px_1px,_currentColor_1px,_transparent_0)] [background-size:14px_14px]"
            aria-hidden
          />
          <div className="relative">
            <p className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] opacity-70">
              <Sparkles className="h-3 w-3" />
              {isEdit ? "Sablon szerkesztése" : "Új shift sablon"}
            </p>
            <h2 className="mt-1.5 truncate text-xl font-semibold tracking-tight">
              {name || "Sablon neve"}
            </h2>
            <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 font-mono text-[12px] tabular-nums">
              <span className="opacity-80">{startTime}–{endTime}</span>
              {Number(unpaidBreak) > 0 && (
                <span className="opacity-60">−{unpaidBreak}m szünet</span>
              )}
              <span className="ml-auto rounded-full bg-foreground/[0.08] px-2 py-0.5 text-[11px] font-semibold opacity-90">
                {fmtHours(duration)}h
              </span>
            </div>
            {siteName && (
              <p className="mt-3 truncate text-[11px] opacity-65">
                Telephely · <span className="font-semibold opacity-90">{siteName}</span>
              </p>
            )}
          </div>
        </div>

        {/* FORM BODY */}
        <div className="space-y-5 px-6 py-5">
          <div className="space-y-1.5">
            <Label htmlFor="tpl-name" className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              Műszak neve
            </Label>
            <Input
              id="tpl-name"
              placeholder="Pl. Délelőtt"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-10"
              data-testid="tpl-name"
            />
          </div>

          {/* Idő-tartomány */}
          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              <Clock className="-mt-0.5 mr-1 inline h-3 w-3" />
              Idő-tartomány
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <TimeBlock label="Kezdés" id="tpl-start" value={startTime} onChange={setStartTime} testId="tpl-start" />
              <TimeBlock label="Vége" id="tpl-end" value={endTime} onChange={setEndTime} testId="tpl-end" />
            </div>
          </div>

          {/* Szünet */}
          <div className="space-y-1.5">
            <Label htmlFor="tpl-break" className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              <Coffee className="-mt-0.5 mr-1 inline h-3 w-3" />
              Fizetetlen szünet
            </Label>
            <div className="flex flex-wrap items-center gap-2">
              {[0, 15, 30, 45, 60].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setUnpaidBreak(m)}
                  className={cn(
                    "rounded-md border px-2.5 py-1 font-mono text-[11px] font-semibold tabular-nums transition-all",
                    Number(unpaidBreak) === m
                      ? "border-foreground bg-foreground text-background"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-border dark:bg-card",
                  )}
                  data-testid={`tpl-break-${m}`}
                >
                  {m === 0 ? "Nincs" : `${m}m`}
                </button>
              ))}
              <Input
                id="tpl-break"
                type="number"
                min={0}
                step={5}
                value={unpaidBreak}
                onChange={(e) => setUnpaidBreak(e.target.value)}
                className="h-8 w-20 text-center font-mono text-[12px]"
                placeholder="perc"
                data-testid="tpl-break"
              />
            </div>
          </div>

          {/* Szín */}
          <div className="space-y-2">
            <Label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              <Palette className="-mt-0.5 mr-1 inline h-3 w-3" />
              Szín
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {SHIFT_COLORS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setColor(c.key)}
                  className={cn(
                    "grid h-8 w-8 place-items-center rounded-full transition-all",
                    c.chip,
                    color === c.key
                      ? "scale-110 ring-2 ring-offset-2 ring-offset-background ring-foreground/80 shadow-sm"
                      : "opacity-70 hover:opacity-100 hover:scale-105",
                  )}
                  aria-label={c.label}
                  data-testid={`tpl-color-${c.key}`}
                />
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="tpl-notes" className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              Megjegyzés (opcionális)
            </Label>
            <Textarea
              id="tpl-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder='Pl. „Csak hétközben" vagy „2 fős műszak"'
              className="resize-none text-[13px]"
              data-testid="tpl-notes"
            />
          </div>
        </div>

        <DialogFooter className="flex items-center border-t border-slate-100 bg-slate-50/60 px-6 py-3 dark:border-border dark:bg-card sm:justify-between">
          {isEdit ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              disabled={deleting || saving}
              className="text-rose-600 hover:bg-rose-500/10 hover:text-rose-700"
              data-testid="tpl-delete"
            >
              {deleting ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="mr-1 h-3.5 w-3.5" />
              )}
              Törlés
            </Button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
              Mégse
            </Button>
            <Button onClick={onSubmit} disabled={saving} data-testid="tpl-save">
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {isEdit ? "Mentés" : "Létrehozás"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TimeBlock({ label, id, value, onChange, testId }) {
  return (
    <label
      htmlFor={id}
      className="group flex cursor-text flex-col rounded-lg border border-slate-200 bg-white px-3 py-2 transition-colors hover:border-slate-300 focus-within:border-foreground focus-within:ring-1 focus-within:ring-foreground/10 dark:border-border dark:bg-card"
    >
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
        {label}
      </span>
      <Input
        id={id}
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-7 border-0 p-0 font-mono text-[15px] font-semibold tabular-nums shadow-none focus-visible:ring-0"
        data-testid={testId}
      />
    </label>
  );
}
